/**
 * API back-office : Espace Formateur (rôle=formateur).
 *
 * Auth = JWT back-office (le formateur a un compte Utilisateur back-office).
 * Tous les endpoints sont préfixés /portail-formateur/me/...
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface FormateurMatiere {
  matiere_id: number;
  code_matiere: string;
  nom_matiere: string;
  niveau: string;
  semestre: number;
  option: string;
  annee: string;
}

export interface FormateurClasse {
  code: string;
  niveau: string;
  option: string;
}

export interface FormateurMeResponse {
  formateur: {
    id: number;
    nom_complet: string;
    email: string;
    role: string;
  };
  matieres: FormateurMatiere[];
  classes: FormateurClasse[];
  kpi: {
    nb_matieres: number;
    nb_classes: number;
    nb_messages_non_lus: number;
  };
}

export interface ActualiteItem {
  id: number;
  titre: string;
  contenu: string;
  type: string;
  service_emetteur: string;
  audience: string;
  epinglee: boolean;
  image_url: string | null;
  date_publication: string | null;
  date_evenement: string | null;
  lieu: string;
}

export interface MessageThreadItem {
  cle: string;
  type: 'individuel' | 'classe';
  matricule: string;
  classe: string;
  dernier_message: string;
  dernier_date: string | null;
  nb_messages: number;
  nb_non_lus_par_formateur: number;
}

export interface MessageItem {
  id: number;
  auteur_type: 'formateur' | 'etudiant';
  auteur_nom: string;
  portee: 'individuel' | 'classe';
  sujet: string;
  contenu: string;
  date_envoi: string;
  lu: boolean;
}

export interface EtudiantItem {
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
}

export interface PlanningSeanceItem {
  id: number;          // négatif si is_virtuel=true (= EDT non encore généré)
  edt_id?: number | null;
  date: string;
  jour: string;
  heure_debut: string;
  heure_fin: string;
  classe: string;
  matiere: string;
  code_matiere?: string;
  salle: string;
  statut: string;
  appel_fait: boolean;
  est_rattrapage: boolean;
  is_virtuel?: boolean;
  is_examen?: boolean;
  session_examen?: {
    id: number;
    nom: string;
    semestre: number;
    type: 'ordinaire' | 'rattrapage';
  };
}

export interface PlanningResponse {
  formateur: string;
  enseignant_id?: number | null;
  periode: { depuis: string; jusqua: string };
  results: PlanningSeanceItem[];
  total: number;
  nb_seances_reelles?: number;
  nb_edts_actifs?: number;
  prochain_cours: (PlanningSeanceItem & { dans_minutes: number }) | null;
  alerte_imminente: boolean;
}

export interface AbsenceDeclareeItem {
  id: number;
  date_absence: string;
  classe: string;
  matiere_libelle: string;
  heure_debut: string | null;
  heure_fin: string | null;
  motif: string;
  peut_rattraper: 'oui' | 'non' | 'a_decider';
  date_rattrapage_proposee: string | null;
  statut: 'declaree' | 'vue_scolarite' | 'rattrapee' | 'annulee';
  note_scolarite: string;
  date_creation: string;
}

export interface DashboardPresencesResponse {
  periode: { depuis: string; jusqua: string };
  classe: string | null;
  kpi: {
    nb_seances: number;
    total_pointages: number;
    taux_presence: number;
    present: number;
    absent: number;
    retard: number;
    justifie: number;
  };
  top_absents: Array<{
    matricule: string;
    nom: string;
    prenoms: string;
    classe: string;
    nb_absences: number;
  }>;
  par_classe: Array<{
    classe: string;
    nb_seances: number;
    total_pointages: number;
    taux_presence: number;
  }>;
  mes_classes?: string[];
}

export interface ConduiteItem {
  id: number;
  date_observation: string;
  categorie: string;
  categorie_label: string;
  type: 'positif' | 'negatif';
  poids: number;
  commentaire: string;
  auteur_nom: string;
  auteur_role: string;
}

export interface MessageParentItem {
  id: number;
  portee: 'individuel' | 'classe';
  matricule_etudiant_cible: string;
  classe: string;
  matiere_libelle: string;
  sujet: string;
  contenu: string;
  date_envoi: string;
}

export interface MatiereNotesItem {
  matiere_id: number;
  code_matiere: string;
  nom_matiere: string;
  niveau: string;
  semestre: number;
  option: string;
  classe: string;
  pas_de_devoir_autorise?: boolean;
  pas_de_devoir_motif?: string;
}

export interface NoteDevoirItem {
  etudiant_id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  note1: number | null;
  note2: number | null;
  absent1: boolean;
  absent2: boolean;
  moy: number | null;
  verrouille: boolean;
}

export interface ActualiteEnrichieItem {
  id: number;
  titre: string;
  contenu: string;
  type: string;
  service_emetteur: string;
  auteur_nom: string;
  auteur_role: string;
  auteur_classe?: string; // Fix 2026-09-07 : classe si l'auteur est étudiant
  audience: string;
  pour_formateurs: boolean;
  epinglee: boolean;
  image_url: string | null;
  date_publication: string | null;
  date_evenement: string | null;
  lieu: string;
  lien_externe: string;
  nb_reactions: number;
  nb_commentaires: number;
  moi_a_reagi: boolean;
}

export interface ReactionAuteurItem {
  type_auteur: 'etudiant' | 'formateur';
  nom_complet: string;
  identifiant: string | number;
  classe?: string;
  photo_url: string | null;
  type_reaction: 'like' | 'coeur' | 'bravo' | 'feu';
  date: string;
}

export interface CommentaireItem {
  id: number;
  parent_id_commentaire: number | null;
  auteur_type: 'etudiant' | 'formateur' | 'admin';
  auteur_nom: string;
  matricule_etudiant: string | null;
  formateur_user_id: number | null;
  contenu: string;
  date_creation: string;
  a_moi: boolean;
}

export interface MessageInterneThread {
  thread_id: number;
  sujet: string;
  dernier_message: string;
  dernier_date: string | null;
  nb_messages: number;
  nb_non_lus: number;
  autres_participants: string[];
  destinataire_role?: string;
  expediteur_role?: string;
}

export interface MessageInterneItem {
  id: number;
  thread_id: number;
  sujet: string;
  contenu: string;
  expediteur_user_id: number;
  expediteur_nom: string;
  expediteur_role: string;
  destinataire_role: string;
  destinataire_nom: string;
  date_envoi: string;
  lu: boolean;
  a_moi: boolean;
}

export interface SessionDepot {
  id: number;
  libelle: string;
  annee_academique: string;
  semestre: 'S1' | 'S2';
  type_session: 'normale' | 'rattrapage';
  date_debut_depot: string | null;
  date_fin_depot: string | null;
  date_examens: string | null;
  statut: 'ouverte' | 'fermee' | 'debloquee' | 'archivee';
  note_admin: string;
  nombre_sujets_requis_par_matiere?: number;
  cree_par_nom: string;
  date_creation: string;
  nb_depots?: number;
  poles?: string[];
  /** IDs des SessionBrassage associés (persistants). Liste vide = agrégation auto. */
  brassage_ids?: number[];
}

export interface BrassageDispoDepot {
  id: number;
  date: string;
  mode: string;
  nombre_etudiants: number;
  nombre_sites_utilises: number;
  description: string;
}

export interface CompletudeItem {
  matiere_id: number | null;
  matiere_libelle: string;
  matiere_code: string;
  niveau: string;
  classe: string;
  option: string;
  semestre: number | null;
  nb_deposes: number;      // total SO + SR
  nb_requis: number;       // total SO_requis + SR_requis
  complet: boolean;
  manquant: number;
  // Compteurs SO/SR distincts (2026-07-17)
  nb_so_deposes?: number;
  nb_so_requis?: number;
  so_complet?: boolean;
  nb_sr_deposes?: number;
  nb_sr_requis?: number;
  sr_complet?: boolean;
}
export interface CompletudeGroupe {
  nom_matiere: string;
  matiere_libelle: string;
  matiere_code: string;
  niveau: string;
  semestre: number | null;
  matieres_ids: number[];
  codes_matiere: string[];
  options: string[];
  classes: string[];        // codes classe : ["L1DI&GL", "L1RT"]
  nb_so_deposes: number;
  nb_sr_deposes: number;
  nb_so_requis: number;
  nb_sr_requis: number;
  so_complet: boolean;
  sr_complet: boolean;
  complet: boolean;
  nb_deposes: number;
  nb_requis: number;
  manquant: number;
}
export interface CompletudeSession {
  total_matieres: number;
  nb_completes: number;
  nb_incompletes: number;
  nb_so_requis?: number;
  nb_sr_requis?: number;
  items: CompletudeItem[];
  // Groupes = même matière+niveau fusionnés (tronc commun)
  groupes?: CompletudeGroupe[];
  total_groupes?: number;
  nb_groupes_completes?: number;
  nb_groupes_incompletes?: number;
}

export interface DepotSujetItem {
  id: number;
  session_id: number;
  matiere_id: number | null;
  matiere_libelle: string;
  matiere_code: string;
  niveau: string;
  classe: string;
  option: string;
  formateur_user_id: number | null;
  formateur_nom: string;
  fichier_url: string | null;
  nom_original: string;
  type_fichier: 'pdf' | 'word' | 'autre';
  taille_octets: number;
  statut: 'depose' | 'verrouille' | 'debloque' | 'annule';
  confidentiel: boolean;
  notes_formateur: string;
  depose_par_nom: string;
  depose_par_role: string;
  date_depot: string;
  date_deblocage: string | null;
  debloque_par_nom: string;
  type_sujet?: 'ordinaire' | 'rattrapage';
}

export interface StatsDepotsSession {
  session: SessionDepot;
  kpi: {
    total_depots: number;
    depose: number;
    verrouille: number;
    debloque: number;
    nb_formateurs_deposants: number;
    nb_formateurs_manquants: number;
  };
  deposants: Array<{
    formateur_user_id: number;
    formateur_nom: string;
    nb_depots: number;
  }>;
  manquants: Array<{
    formateur_user_id: number;
    formateur_nom: string;
    email: string;
    nb_attendus: number;
    nb_deposes: number;
    matieres_non_deposees: Array<{ matiere_id: number; matiere_libelle: string; niveau: string; semestre: number }>;
  }>;
}

export interface SujetExamenItem {
  id: number;
  titre: string;
  description: string;
  matiere_libelle: string;
  matiere_code: string;
  niveau: string;
  option: string;
  annee_academique: string;
  semestre: string;
  session: 'normale' | 'rattrapage';
  fichier_url: string | null;
  nom_original: string;
  taille_octets: number;
  taille_humaine: string;
  uploade_par_nom: string;
  date_creation: string;
  nb_telechargements: number;
}

export interface SupportCoursItem {
  id: number;
  titre: string;
  description: string;
  nom_original: string;
  type_fichier: 'pdf' | 'word' | 'autre';
  taille_octets: number;
  taille_humaine: string;
  fichier_url: string | null;
  matiere_id: number | null;
  matiere_libelle: string;
  niveau: string;
  semestre: number | null;
  option: string;
  ordre: number;
  formateur_nom: string;
  nb_telechargements: number;
  date_creation: string;
}

// ─── Audit téléchargements sujets (fuite du 17/08 — fix 2026-08-27) ─────────
export interface AuditTelechargementsParams {
  depuis?: string;   // YYYY-MM-DD
  jusqu?: string;    // YYYY-MM-DD
  user_id?: number;
  depot_id?: number;
  limit?: number;
}
export interface AuditTelechargementLigne {
  id: number;
  date: string;
  user_id: number | null;
  user_nom: string;
  user_username: string;
  user_role: string;
  depot_id: number | null;
  detail: string;
  ip: string;
  user_agent: string;
}
export interface AuditTopUser {
  user_id: number | null;
  nom_complet: string;
  username: string;
  role: string;
  nb_telechargements: number;
}
export interface AuditTopDepot {
  depot_id: number | null;
  nb_telechargements: number;
}
export interface AuditTelechargementsResponse {
  total: number;
  nb_retour: number;
  items: AuditTelechargementLigne[];
  top_users: AuditTopUser[];
  top_depots: AuditTopDepot[];
}

// ─── Statistiques dashboard formateur (fix 2026-09-02) ─────────────────────

export interface StatsMatiereItem {
  matiere_id: number;
  code_matiere: string;
  nom_matiere: string;
  classe: string;
  niveau: string;
  option: string;
  semestre: string;
  annee: string;
  nb_etudiants: number;
  nb_notes_devoir: number;
  nb_notes_examen: number;
  nb_admis_devoir: number;
  nb_admis_examen: number;
  taux_succes_devoir: number;
  taux_succes_examen: number;
  moy_classe_devoir: number;
  moy_classe_examen: number;
  distribution_devoir: Record<string, number>;
  distribution_examen: Record<string, number>;
}

export interface StatsClasseItem {
  classe: string;
  nb_matieres: number;
  nb_etudiants: number;
  taux_succes_moyen: number;
  moy_classe: number;
}

export interface StatsFormateurResponse {
  matieres: StatsMatiereItem[];
  totaux: {
    nb_classes: number;
    nb_matieres: number;
    nb_etudiants_total: number;
    taux_succes_global: number;
    taux_succes_devoir: number;
    taux_succes_examen: number;
    moy_globale_devoir: number;
    moy_globale_examen: number;
  };
  top_matieres: StatsMatiereItem[];
  comparaison_classes: StatsClasseItem[];
  semestre_filtre: string;
}

// ─── Service ────────────────────────────────────────────────────────────────

export const formateurApi = createApi({
  reducerPath: 'formateurApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: [
    'FormateurMe', 'FormateurMessages', 'FormateurActualites', 'FormateurSupports',
    'FormateurPlanning', 'FormateurAbsences', 'FormateurDashboard',
    'FormateurConduite', 'FormateurMessagesParents', 'FormateurNotes',
    'FormateurActuFil', 'FormateurCommentaires', 'FormateurMsgInterne',
    'FormateurSujets', 'FormateurSessions', 'FormateurDepots',
  ],
  endpoints: (builder) => ({
    me: builder.query<FormateurMeResponse, void>({
      query: () => '/portail-formateur/me/',
      providesTags: ['FormateurMe'],
    }),

    mesEtudiants: builder.query<{
      results: EtudiantItem[];
      total: number;
      classes_accessibles: string[];
    }, { classe?: string } | void>({
      query: (params) => ({
        url: '/portail-formateur/me/etudiants/',
        params: params ?? undefined,
      }),
    }),

    actualitesFormateur: builder.query<{ results: ActualiteItem[]; total: number }, void>({
      query: () => '/portail-formateur/me/actualites/',
      providesTags: ['FormateurActualites'],
    }),

    listerMessagesFormateur: builder.query<{ results: MessageThreadItem[]; total: number }, void>({
      query: () => '/portail-formateur/me/messages/',
      providesTags: ['FormateurMessages'],
    }),

    envoyerMessageFormateur: builder.mutation<{
      success: boolean;
      id: number;
      portee: 'individuel' | 'classe';
      classe: string;
      matricule_etudiant_destinataire: string;
    }, {
      matricule_etudiant?: string;
      classe?: string;
      sujet?: string;
      contenu: string;
      matiere_libelle?: string;
      matiere_id?: number;
    }>({
      query: (body) => ({
        url: '/portail-formateur/me/messages/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FormateurMessages'],
    }),

    messageThreadFormateur: builder.query<{ results: MessageItem[]; total: number }, {
      matricule?: string; classe?: string;
    }>({
      query: (params) => ({
        url: '/portail-formateur/me/messages/thread/',
        params,
      }),
      providesTags: ['FormateurMessages'],
    }),

    // Supports de cours — listing du formateur
    listerMesSupports: builder.query<{ results: SupportCoursItem[]; total: number }, {
      matiere_id?: number; niveau?: string;
    } | void>({
      query: (params) => ({
        url: '/portail-formateur/me/supports/',
        params: params ?? undefined,
      }),
      providesTags: ['FormateurSupports'],
    }),

    // Upload (multipart) — utiliser FormData
    uploaderSupport: builder.mutation<SupportCoursItem, FormData>({
      query: (formData) => ({
        url: '/portail-formateur/me/supports/',
        method: 'POST',
        body: formData,
        // Le navigateur définit automatiquement le Content-Type multipart avec boundary
      }),
      invalidatesTags: ['FormateurSupports'],
    }),

    modifierSupport: builder.mutation<SupportCoursItem, {
      id: number;
      titre?: string;
      description?: string;
      ordre?: number;
    }>({
      query: ({ id, ...body }) => ({
        url: `/portail-formateur/me/supports/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['FormateurSupports'],
    }),

    supprimerSupport: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({
        url: `/portail-formateur/me/supports/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FormateurSupports'],
    }),

    // ─── Planning hebdomadaire + alertes ─────────────────────────────────
    monPlanning: builder.query<PlanningResponse, { depuis?: string; jusqua?: string } | void>({
      query: (params) => ({ url: '/portail-formateur/me/planning/', params: params ?? undefined }),
      providesTags: ['FormateurPlanning'],
    }),

    // ─── Mes examens à venir (EDT publiés) ───────────────────────────────
    mesExamensAVenir: builder.query<{
      formateur: string;
      depuis: string;
      total: number;
      results: Array<{
        creneau_id: number;
        date: string; jour_semaine: string; jours_avant: number;
        heure_debut: string; heure_fin: string;
        matiere: string; niveau: string; groupe_label: string;
        session_id: number; session_nom: string;
        session_type: 'ordinaire' | 'rattrapage';
        session_semestre: number;
        enseignant_noms: string[];
      }>;
      sessions: Array<{
        id: number; nom: string; type: string; semestre: number;
        annee_academique: string;
        date_debut: string | null; date_fin: string | null;
        nb_creneaux_moi: number;
      }>;
    }, { depuis?: string } | void>({
      query: (params) => ({
        url: '/portail-formateur/me/examens-a-venir/',
        params: params ?? undefined,
      }),
      providesTags: ['FormateurPlanning'],
    }),

    // ─── Absences déclarées ───────────────────────────────────────────────
    listerMesAbsences: builder.query<{ results: AbsenceDeclareeItem[]; total: number }, void>({
      query: () => '/portail-formateur/me/absences/',
      providesTags: ['FormateurAbsences'],
    }),
    declarerAbsence: builder.mutation<{
      success: boolean; id: number; nb_seances_impactees: number; message: string;
    }, {
      date_absence: string;
      classe?: string;
      matiere_libelle?: string;
      heure_debut?: string;
      heure_fin?: string;
      motif: string;
      peut_rattraper?: 'oui' | 'non' | 'a_decider';
      date_rattrapage_proposee?: string;
    }>({
      query: (body) => ({
        url: '/portail-formateur/me/absences/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FormateurAbsences', 'FormateurPlanning'],
    }),
    annulerSeance: builder.mutation<{ success: boolean; message: string }, {
      seanceId: number; motif?: string;
    }>({
      query: ({ seanceId, motif }) => ({
        url: `/portail-formateur/me/seances/${seanceId}/annuler/`,
        method: 'POST',
        body: { motif },
      }),
      invalidatesTags: ['FormateurPlanning', 'FormateurAbsences'],
    }),
    reporterSeanceFormateur: builder.mutation<{
      success: boolean; message: string; rattrapage_id: number;
    }, {
      seanceId: number;
      nouvelle_date: string;
      nouvelle_heure_debut?: string;
      nouvelle_heure_fin?: string;
      nouvelle_salle?: string;
      motif?: string;
    }>({
      query: ({ seanceId, ...body }) => ({
        url: `/portail-formateur/me/seances/${seanceId}/reporter/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FormateurPlanning'],
    }),

    annulerMonAbsence: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({
        url: `/portail-formateur/me/absences/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FormateurAbsences', 'FormateurPlanning'],
    }),

    // ─── Dashboard présences/absences ────────────────────────────────────
    dashboardPresencesFormateur: builder.query<DashboardPresencesResponse, {
      depuis?: string; jusqua?: string; classe?: string;
    } | void>({
      query: (params) => ({ url: '/portail-formateur/me/dashboard-presences/',
                            params: params ?? undefined }),
      providesTags: ['FormateurDashboard'],
    }),

    // ─── Conduite ─────────────────────────────────────────────────────────
    conduiteEtudiantFormateur: builder.query<{
      etudiant: { matricule: string; nom: string; prenoms: string; classe: string };
      items: ConduiteItem[];
      total: number;
    }, string>({
      query: (matricule) => `/portail-formateur/me/conduite/${matricule}/`,
      providesTags: ['FormateurConduite'],
    }),
    creerConduiteFormateur: builder.mutation<{ success: boolean; id: number }, {
      matricule: string;
      categorie?: string;
      type: 'positif' | 'negatif';
      poids: number;
      commentaire: string;
    }>({
      query: (body) => ({
        url: '/portail-formateur/me/conduite/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FormateurConduite'],
    }),

    // ─── Messages aux parents ────────────────────────────────────────────
    listerMessagesParents: builder.query<{ results: MessageParentItem[]; total: number }, void>({
      query: () => '/portail-formateur/me/messages-parents/',
      providesTags: ['FormateurMessagesParents'],
    }),
    envoyerMessageParent: builder.mutation<{
      success: boolean; id: number; portee: string; classe: string;
    }, {
      matricule_etudiant_cible?: string;
      classe?: string;
      sujet?: string;
      contenu: string;
      matiere_libelle?: string;
    }>({
      query: (body) => ({
        url: '/portail-formateur/me/messages-parents/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FormateurMessagesParents'],
    }),

    // ─── Saisie notes devoir ──────────────────────────────────────────────
    mesMatieresNotes: builder.query<{ results: MatiereNotesItem[]; total: number }, void>({
      query: () => '/portail-formateur/me/matieres-notes/',
      providesTags: ['FormateurNotes'],
    }),
    notesDevoirMatiere: builder.query<{
      matiere: { id: number; code: string; nom: string; niveau: string; semestre: number };
      classe: string;
      annee_academique: string;
      semestre_str: string;
      saisie_autorisee: boolean;
      fenetre: {
        mode: 'ouverte' | 'aucune' | 'cible_exclu' | 'no_module';
        message: string;
        libelle?: string;
        date_debut?: string;
        date_fin?: string;
      };
      items: NoteDevoirItem[];
      total: number;
    }, { matiere_id: number; classe: string }>({
      query: ({ matiere_id, classe }) =>
        `/portail-formateur/me/notes/${matiere_id}/${classe}/`,
      providesTags: ['FormateurNotes'],
    }),
    enregistrerNotesDevoir: builder.mutation<{
      success: boolean; nb_enregistrees: number; erreurs: any[];
    }, {
      matiere_id: number;
      classe: string;
      notes: Array<{
        etudiant_id: number;
        note1?: number | null;
        note2?: number | null;
        absent1?: boolean;
        absent2?: boolean;
      }>;
    }>({
      query: ({ matiere_id, classe, notes }) => ({
        url: `/portail-formateur/me/notes/${matiere_id}/${classe}/`,
        method: 'POST',
        body: { notes },
      }),
      invalidatesTags: ['FormateurNotes'],
    }),

    // ─── Signaler qu'aucun devoir ne sera fait sur cette matière ─────────
    signalerPasDeDevoir: builder.mutation<
      { success: boolean; pas_de_devoir_autorise: boolean; matiere_id: number; nb_attributions_maj: number },
      { matiere_id: number; actif: boolean; motif?: string; force?: boolean }
    >({
      query: ({ matiere_id, actif, motif, force }) => ({
        url: `/portail-formateur/me/matieres/${matiere_id}/signaler-pas-de-devoir/`,
        method: 'POST',
        body: { actif, motif: motif || '', force: !!force },
      }),
      invalidatesTags: ['FormateurNotes'],
    }),

    // ─── Photo profil ────────────────────────────────────────────────────
    uploaderMaPhoto: builder.mutation<{ success: boolean; photo_url: string | null }, FormData>({
      query: (formData) => ({
        url: '/portail-formateur/me/photo/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['FormateurMe'],
    }),
    supprimerMaPhoto: builder.mutation<{ success: boolean }, void>({
      query: () => ({ url: '/portail-formateur/me/photo/', method: 'DELETE' }),
      invalidatesTags: ['FormateurMe'],
    }),

    // ─── Statistiques dashboard (fix 2026-09-02) ─────────────────────────
    mesStatistiques: builder.query<StatsFormateurResponse, { semestre?: string } | void>({
      query: (params) => ({
        url: '/portail-formateur/me/statistiques/',
        params: params ?? undefined,
      }),
    }),

    // ─── Actualités enrichies (fil) ──────────────────────────────────────
    actualitesFil: builder.query<{ results: ActualiteEnrichieItem[]; total: number }, void>({
      query: () => '/portail-formateur/me/actualites-fil/',
      providesTags: ['FormateurActuFil'],
    }),
    reagirActualite: builder.mutation<{ success: boolean; type_reaction: string }, {
      actualiteId: number;
      type_reaction?: 'like' | 'coeur' | 'bravo' | 'feu';
    }>({
      query: ({ actualiteId, type_reaction }) => ({
        url: `/portail-formateur/me/actualites/${actualiteId}/reaction/`,
        method: 'POST',
        body: { type_reaction: type_reaction || 'like' },
      }),
      invalidatesTags: ['FormateurActuFil'],
    }),
    retirerReactionActualite: builder.mutation<{ success: boolean }, number>({
      query: (actualiteId) => ({
        url: `/portail-formateur/me/actualites/${actualiteId}/reaction/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FormateurActuFil'],
    }),
    commentairesActualite: builder.query<{ results: CommentaireItem[]; total: number }, number>({
      query: (actualiteId) => `/portail-formateur/me/actualites/${actualiteId}/commentaires/`,
      providesTags: (_r, _e, id) => [{ type: 'FormateurCommentaires', id }],
    }),
    reactionsActualite: builder.query<{
      results: ReactionAuteurItem[];
      total: number;
      par_type: Record<string, number>;
    }, number>({
      query: (actualiteId) => `/portail-formateur/me/actualites/${actualiteId}/reactions/`,
      providesTags: (_r, _e, id) => [{ type: 'FormateurActuFil', id }],
    }),
    ajouterCommentaire: builder.mutation<{ success: boolean; id: number }, {
      actualiteId: number; contenu: string; parent_id_commentaire?: number | null;
    }>({
      query: ({ actualiteId, contenu, parent_id_commentaire }) => ({
        url: `/portail-formateur/me/actualites/${actualiteId}/commentaires/`,
        method: 'POST',
        body: { contenu, parent_id_commentaire },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FormateurCommentaires', id: arg.actualiteId },
        'FormateurActuFil',
      ],
    }),
    supprimerCommentaire: builder.mutation<{ success: boolean }, {
      commentaireId: number; actualiteId: number;
    }>({
      query: ({ commentaireId }) => ({
        url: `/portail-formateur/me/commentaires/${commentaireId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FormateurCommentaires', id: arg.actualiteId },
        'FormateurActuFil',
      ],
    }),

    // ─── Publication actualité ──────────────────────────────────────────
    publierActualite: builder.mutation<{ success: boolean; id: number }, FormData>({
      query: (formData) => ({
        url: '/portail-formateur/me/publications/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['FormateurActuFil'],
    }),
    supprimerMaPublication: builder.mutation<{ success: boolean }, number>({
      query: (actualiteId) => ({
        url: `/portail-formateur/me/publications/${actualiteId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FormateurActuFil'],
    }),

    // ─── Messagerie interne ──────────────────────────────────────────────
    mesMessagesInternes: builder.query<{ results: MessageInterneThread[]; total: number }, void>({
      query: () => '/portail-formateur/messagerie-interne/',
      providesTags: ['FormateurMsgInterne'],
    }),
    threadMessageInterne: builder.query<{ results: MessageInterneItem[]; total: number }, number>({
      query: (threadId) => `/portail-formateur/messagerie-interne/thread/${threadId}/`,
      providesTags: (_r, _e, id) => [{ type: 'FormateurMsgInterne', id }],
    }),
    envoyerMessageInterne: builder.mutation<{
      success: boolean; id: number; thread_id: number;
    }, {
      destinataire_role: 'surveillance' | 'direction' | 'scolarite' | 'formateur';
      destinataire_user_id?: number | null;
      sujet?: string;
      contenu: string;
      thread_id?: number | null;
    }>({
      query: (body) => ({
        url: '/portail-formateur/messagerie-interne/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FormateurMsgInterne'],
    }),

    // ─── Banque sujets examens (admin/scolarité) ─────────────────────────
    sujetsExamensAdmin: builder.query<{ results: SujetExamenItem[]; total: number }, {
      matiere?: string; niveau?: string; annee_academique?: string; option?: string;
    } | void>({
      query: (params) => ({
        url: '/portail-formateur/admin/sujets-examens/',
        params: params ?? undefined,
      }),
      providesTags: ['FormateurSujets'],
    }),
    uploaderSujetExamen: builder.mutation<{
      success: boolean; id: number; sujet: SujetExamenItem;
    }, FormData>({
      query: (formData) => ({
        url: '/portail-formateur/admin/sujets-examens/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['FormateurSujets'],
    }),
    supprimerSujetExamen: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({
        url: `/portail-formateur/admin/sujets-examens/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FormateurSujets'],
    }),

    // ─── Sessions de dépôt sujets ────────────────────────────────────────
    sessionsDepot: builder.query<{ results: SessionDepot[]; total: number }, {
      statut?: string; actives_seulement?: boolean;
    } | void>({
      query: (params) => ({ url: '/portail-formateur/sessions-depot-sujets/',
                            params: params ?? undefined }),
      providesTags: ['FormateurSessions'],
    }),
    creerSessionDepot: builder.mutation<SessionDepot, {
      libelle: string;
      annee_academique: string;
      semestre: 'S1' | 'S2';
      type_session: 'normale' | 'rattrapage';
      date_debut_depot?: string;
      date_fin_depot?: string;
      date_examens?: string;
      note_admin?: string;
      nombre_sujets_requis_par_matiere?: number;
      poles?: string[];
    }>({
      query: (body) => ({
        url: '/portail-formateur/sessions-depot-sujets/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FormateurSessions'],
    }),
    updateSessionDepot: builder.mutation<SessionDepot, {
      id: number;
      statut?: 'ouverte' | 'fermee' | 'debloquee' | 'archivee';
      libelle?: string;
      note_admin?: string;
      /** IDs des brassages à utiliser pour les effectifs par site.
       *  Envoyer une liste vide `[]` pour repasser en mode agrégation auto. */
      brassage_ids?: number[];
    }>({
      query: ({ id, ...body }) => ({
        url: `/portail-formateur/sessions-depot-sujets/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['FormateurSessions', 'FormateurDepots'],
    }),
    supprimerSessionDepot: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({
        url: `/portail-formateur/sessions-depot-sujets/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FormateurSessions'],
    }),

    // ─── Dépôts (formateur) ──────────────────────────────────────────────
    mesDepotsSujets: builder.query<{
      results: DepotSujetItem[]; total: number;
      sessions_ouvertes: SessionDepot[];
      mes_matieres: Array<{ matiere_id: number; nom_matiere: string; code_matiere: string;
                            niveau: string; semestre: number; option: string }>;
      mes_classes: Array<{ code: string; niveau: string; option: string }>;
      completude_par_session?: Record<number, CompletudeSession>;
    }, { session_id?: number } | void>({
      query: (params) => ({ url: '/portail-formateur/me/depots-sujets/',
                            params: params ?? undefined }),
      providesTags: ['FormateurDepots'],
    }),
    deposerSujet: builder.mutation<{ success: boolean; id: number; depot: DepotSujetItem }, {
      sessionId: number; formData: FormData;
    }>({
      query: ({ sessionId, formData }) => ({
        url: `/portail-formateur/me/depots-sujets/session/${sessionId}/`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['FormateurDepots'],
    }),
    annulerMonDepot: builder.mutation<{ success: boolean }, number>({
      query: (depotId) => ({
        url: `/portail-formateur/me/depots-sujets/${depotId}/annuler/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FormateurDepots'],
    }),

    // ─── Dépôts (admin/scolarité) ───────────────────────────────────────
    adminDepotsSujets: builder.query<{
      results: DepotSujetItem[];
      total: number;
      groupes?: Array<{
        formateur_user_id: number | null;
        formateur_nom: string;
        matiere_libelle: string;
        matiere_code: string;
        niveau: string;
        type_sujet: 'ordinaire' | 'rattrapage';
        classes: string[];
        options: string[];
        depots: DepotSujetItem[];
        ids: number[];
        date_derniere: string | null;
        statut_agrege: string;
        /** Effectifs par site basés sur le brassage de la session. Aide à
         *  imprimer le bon nombre de sujets. */
        sites_effectifs?: { site_nom: string; effectif: number }[];
        effectif_total?: number;
        brassage_source?: boolean;
      }>;
      total_groupes?: number;
      brassage_disponible?: boolean;
      /** Brassages qui ont fourni des placements dans la réponse actuelle */
      brassages_utilises?: Array<{
        id: number; date: string; mode: string;
        nombre_etudiants: number; description: string;
      }>;
      /** Liste des brassages de l'année pour le sélecteur UI */
      brassages_disponibles?: BrassageDispoDepot[];
      /** IDs actuellement persistés sur la session (source de vérité UI) */
      brassage_ids_persistes?: number[];
    }, {
      session_id?: number; formateur_user_id?: number; statut?: string; niveau?: string;
    } | void>({
      query: (params) => ({ url: '/portail-formateur/admin/depots-sujets/',
                            params: params ?? undefined }),
      providesTags: ['FormateurDepots'],
    }),
    adminUploaderDepot: builder.mutation<{ success: boolean; id: number }, FormData>({
      query: (formData) => ({
        url: '/portail-formateur/admin/depots-sujets/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['FormateurDepots'],
    }),
    adminActionDepot: builder.mutation<{ success: boolean; depot: DepotSujetItem }, {
      depotId: number;
      action: 'verrouiller' | 'debloquer' | 'reverrouiller';
    }>({
      query: ({ depotId, action }) => ({
        url: `/portail-formateur/admin/depots-sujets/${depotId}/`,
        method: 'PATCH',
        body: { action },
      }),
      invalidatesTags: ['FormateurDepots'],
    }),
    adminSupprimerDepot: builder.mutation<{ success: boolean }, number>({
      query: (depotId) => ({
        url: `/portail-formateur/admin/depots-sujets/${depotId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FormateurDepots'],
    }),
    statsDepotsSession: builder.query<StatsDepotsSession, number>({
      query: (sessionId) => `/portail-formateur/admin/depots-sujets/stats/${sessionId}/`,
      providesTags: ['FormateurDepots'],
    }),
    badgeNouveauxDepots: builder.query<{ nb_nouveaux: number }, void>({
      query: () => '/portail-formateur/admin/depots-sujets/badge/',
      providesTags: ['FormateurDepots'],
    }),

    // ── AUDIT téléchargements sujets (fix 2026-08-27 fuite du 17/08) ─────
    auditTelechargementsSujets: builder.query<AuditTelechargementsResponse, AuditTelechargementsParams>({
      query: (params = {}) => ({
        url: '/portail-formateur/admin/depots-sujets/audit-telechargements/',
        params,
      }),
    }),

    // ── Web Push (PWA) ────────────────────────────────────────────────────
    getPushVapidKeyFormateur: builder.query<{ public_key: string }, void>({
      query: () => '/portail-formateur/push/vapid-key/',
    }),
    pushSubscribeFormateur: builder.mutation<
      { id: number; created: boolean; actif: boolean },
      { endpoint: string; keys: { p256dh: string; auth: string }; user_agent?: string }
    >({
      query: (body) => ({
        url: '/portail-formateur/push/subscribe/',
        method: 'POST',
        body,
      }),
    }),
    pushUnsubscribeFormateur: builder.mutation<{ desactivees: number }, { endpoint: string }>({
      query: (body) => ({
        url: '/portail-formateur/push/unsubscribe/',
        method: 'POST',
        body,
      }),
    }),
    pushTestFormateur: builder.mutation<{ envoyees: number }, void>({
      query: () => ({
        url: '/portail-formateur/push/test/',
        method: 'POST',
      }),
    }),

    // ─── Fusion de sessions de dépôt (2026-07-22) ────────────────────────
    apercuFusionSessions: builder.query<{
      source: SessionDepot;
      cible: SessionDepot;
      nb_deplacables: number;
      nb_doublons: number;
      apercu_deplacables: Array<{ id: number; matiere: string; formateur: string; type_sujet: string; option: string }>;
      apercu_doublons: Array<{ src_id: number; dst_ids: number[]; gardera_source: boolean; matiere: string; formateur: string; type_sujet: string; option: string }>;
    }, { source_id: number; cible_id: number }>({
      query: ({ source_id, cible_id }) => `/portail-formateur/sessions-depot-sujets/${source_id}/fusionner/${cible_id}/`,
    }),
    fusionnerSessions: builder.mutation<{
      success: boolean;
      nb_deplaces: number;
      nb_doublons_supprimes: number;
      session_source_libelle: string;
      session_cible_id: number;
      message: string;
    }, { source_id: number; cible_id: number }>({
      query: ({ source_id, cible_id }) => ({
        url: `/portail-formateur/sessions-depot-sujets/${source_id}/fusionner/${cible_id}/`,
        method: 'POST',
        body: { confirmer: true },
      }),
      invalidatesTags: ['FormateurDepots'],
    }),

    // ─── Demandes de modification de dépôt (verrou 48h — 2026-07-22) ─────
    demanderModifDepot: builder.mutation<{
      success: boolean; demande_id: number; statut: string; message: string;
    }, { depot_id: number; motif: string }>({
      query: ({ depot_id, motif }) => ({
        url: `/portail-formateur/me/depots-sujets/${depot_id}/demander-modification/`,
        method: 'POST',
        body: { motif },
      }),
      invalidatesTags: ['FormateurDepots'],
    }),
    mesDemandesModif: builder.query<{
      results: Array<{
        id: number; depot_id: number; matiere_libelle: string; niveau: string;
        motif: string; statut: string;
        date_demande: string; date_decision: string | null;
        decidee_par_nom: string; reponse_scolarite: string;
        autorisation_expire_le: string | null;
      }>;
      total: number;
    }, void>({
      query: () => '/portail-formateur/me/demandes-modification/',
      providesTags: ['FormateurDepots'],
    }),
    adminDemandesModif: builder.query<{
      results: Array<{
        id: number; depot_id: number;
        matiere_libelle: string; matiere_code: string; niveau: string;
        classe: string; option: string; type_sujet: string;
        formateur_user_id: number; formateur_nom: string;
        motif: string; statut: string;
        date_demande: string; date_decision: string | null;
        decidee_par_nom: string; reponse_scolarite: string;
        autorisation_expire_le: string | null;
      }>;
      total: number;
      nb_en_attente: number;
    }, { statut?: string } | void>({
      query: (params) => ({
        url: '/portail-formateur/admin/demandes-modification/',
        params: params ?? undefined,
      }),
      providesTags: ['FormateurDepots'],
    }),
    adminDeciderDemandeModif: builder.mutation<{
      success: boolean; statut: string; autorisation_expire_le: string | null; message: string;
    }, { demande_id: number; decision: 'accepter' | 'refuser'; reponse?: string; fenetre_heures?: number }>({
      query: ({ demande_id, ...body }) => ({
        url: `/portail-formateur/admin/demandes-modification/${demande_id}/decider/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FormateurDepots'],
    }),
  }),
});

export const {
  useMeQuery: useFormateurMeQuery,
  useMesEtudiantsQuery,
  useActualitesFormateurQuery,
  useListerMessagesFormateurQuery,
  useEnvoyerMessageFormateurMutation,
  useMessageThreadFormateurQuery,
  useListerMesSupportsQuery,
  useUploaderSupportMutation,
  useModifierSupportMutation,
  useSupprimerSupportMutation,
  // Nouveaux endpoints
  useMonPlanningQuery,
  useMesExamensAVenirQuery,
  useListerMesAbsencesQuery,
  useDeclarerAbsenceMutation,
  useAnnulerMonAbsenceMutation,
  useAnnulerSeanceMutation,
  useReporterSeanceFormateurMutation,
  useDashboardPresencesFormateurQuery,
  useConduiteEtudiantFormateurQuery,
  useCreerConduiteFormateurMutation,
  useListerMessagesParentsQuery,
  useEnvoyerMessageParentMutation,
  useMesMatieresNotesQuery,
  useNotesDevoirMatiereQuery,
  useEnregistrerNotesDevoirMutation,
  useSignalerPasDeDevoirMutation,
  // Photo
  useUploaderMaPhotoMutation,
  useSupprimerMaPhotoMutation,
  // Statistiques dashboard (2026-09-02)
  useMesStatistiquesQuery,
  // Actualités fil
  useActualitesFilQuery,
  useReagirActualiteMutation,
  useRetirerReactionActualiteMutation,
  useCommentairesActualiteQuery,
  useReactionsActualiteQuery,
  useAjouterCommentaireMutation,
  useSupprimerCommentaireMutation,
  // Publication
  usePublierActualiteMutation,
  useSupprimerMaPublicationMutation,
  // Messagerie interne
  useMesMessagesInternesQuery,
  useThreadMessageInterneQuery,
  useEnvoyerMessageInterneMutation,
  // Sujets examens admin
  useSujetsExamensAdminQuery,
  useUploaderSujetExamenMutation,
  useSupprimerSujetExamenMutation,
  // Sessions + dépôts sujets
  useSessionsDepotQuery,
  useCreerSessionDepotMutation,
  useUpdateSessionDepotMutation,
  useSupprimerSessionDepotMutation,
  useMesDepotsSujetsQuery,
  useDeposerSujetMutation,
  useAnnulerMonDepotMutation,
  useAdminDepotsSujetsQuery,
  useAdminUploaderDepotMutation,
  useAdminActionDepotMutation,
  useAdminSupprimerDepotMutation,
  useStatsDepotsSessionQuery,
  useBadgeNouveauxDepotsQuery,
  useAuditTelechargementsSujetsQuery,
  // Web Push
  useGetPushVapidKeyFormateurQuery,
  useLazyGetPushVapidKeyFormateurQuery,
  usePushSubscribeFormateurMutation,
  usePushUnsubscribeFormateurMutation,
  usePushTestFormateurMutation,
  // Fusion sessions
  useApercuFusionSessionsQuery,
  useLazyApercuFusionSessionsQuery,
  useFusionnerSessionsMutation,
  // Demandes de modification (verrou 48h)
  useDemanderModifDepotMutation,
  useMesDemandesModifQuery,
  useAdminDemandesModifQuery,
  useAdminDeciderDemandeModifMutation,
} = formateurApi;
